import {
  demoNovel,
  isValidSupportLink,
  stripStateSystem,
  localMvpOwnerId,
  normalizeNovel,
  parseOAuthProviders,
  type Asset,
  type Emotion,
  type GenerationJob,
  type Novel,
  type OAuthProvider
} from "@lorevok/domain";
import { getAccessToken, isAuthEnabled } from "./lib/supabaseClient.js";
import { takePrefetched } from "./lib/prefetchRoutes.js";
import { rememberNovelLink, rememberNovelLinks } from "./lib/novelLinks.js";
import { isRestartStatus, reportApiTrouble } from "./lib/apiStatus.js";
import { catalogFilterApiParams, hasCatalogFilters, type CatalogFilters } from "./lib/catalogFilters.js";
import {
  CATALOG_CACHE_KEY,
  apiCacheRevision,
  apiIdentityRevision,
  clearApiCache,
  invalidateReadMemory,
  invalidateListCaches,
  myNovelsCacheKey,
  readApiCache,
  readNovelCache,
  removeApiCache,
  removeNovelCache,
  writeApiCache,
  writeNovelCache
} from "./lib/apiCache.js";
import { safeLocalStorageGet, safeLocalStorageSet } from "./lib/storage.js";

export interface NovelStats {
  views: number;
  likes: number;
  /** Distinct readers who reached an ending. */
  completions: number;
}

/** Zeroed stats — the offline/fallback and pre-load default. */
export const EMPTY_STATS: NovelStats = {
  views: 0,
  likes: 0,
  completions: 0
};

export interface NovelSummary {
  id: string;
  title: string;
  description: string;
  visibility: Novel["visibility"];
  genre: string[];
  tone: string;
  cover_asset_id: string;
  cover_asset_url: string | null;
  owner_id: string;
  /** The author's public display name, for the "by ‹name›" byline. Optional
   *  because a summary cached before this field existed (or the demo fallback)
   *  lacks it. */
  owner_display_name?: string;
  /** The author's permanent username: their page's address, /u/<username>. */
  owner_username?: string;
  /** The author's avatar URL for the byline; null/absent when none is set. */
  owner_avatar_url?: string | null;
  updated_at: string;
  views: number;
  likes: number;
  /** Distinct readers who reached an ending. */
  completions: number;
  /** The novel's own language code ("en", "ru"), shown as a chip on its card.
   *  Optional because summaries cached before this field existed lack it. */
  language?: string;
  /** Language codes this novel is machine-translated into (empty/absent = single-language).
   *  Optional because summaries cached before this field existed lack it. */
  available_languages?: string[];
  /** Permanent link code (`/novel/<slug>-<code>`); absent on the demo and before the API sends it. */
  short_code?: string;
}

/**
 * Where a read's data came from: a live API answer, the offline cache
 * ("cache" = the API was unreachable and this is the last successful copy),
 * or the built-in fallback (demo novel / empty list).
 */
export type NovelListSource = "api" | "fallback" | "cache";

export interface NovelCatalogResult {
  novels: NovelSummary[];
  source: NovelListSource;
  nextOffset?: number | null;
}

export interface NovelResult {
  novel: Novel;
  stats: NovelStats;
  /** The author's public display name, for the novel-detail byline. */
  ownerDisplayName?: string;
  /** The author's username, for the link to their page. */
  ownerUsername?: string;
  /** The author's avatar URL, for the novel-detail byline. */
  ownerAvatarUrl?: string | null;
  /** Whether the signed-in account likes this novel (the server's answer, per account). */
  likedByMe?: boolean;
  source: NovelListSource;
  /**
   * With `source: "fallback"`: the API gave no answer at all (offline, a
   * timeout, a 5xx) rather than a definitive "no such novel". A page must not
   * tell search engines a novel is gone on the strength of a blip.
   */
  unavailable?: boolean;
}

/** The editable/public fields of an author profile. */
export interface ProfileFields {
  id: string;
  /** Permanent, unique handle set at sign-up: the page's address, /u/<username>. */
  username: string;
  displayName: string;
  avatarUrl: string | null;
  bio: string;
  /** "Support the author" link (Boosty, Ko-fi, …) shown on the author page; "" when unset. */
  supportLink: string;
  /** ISO timestamp the profile was created ("member since"), or null. */
  createdAt: string | null;
}

/** A public author profile: profile fields + the author's published novels. */
export interface AuthorProfileResult extends ProfileFields {
  novels: NovelSummary[];
  /** The API has no author under this key (a mistyped or stale /u/ link). */
  notFound?: boolean;
  /** The API gave no answer (offline, a timeout, a 5xx): the empty profile above is a stand-in, not a fact. */
  unavailable?: boolean;
}

/** Parses the server's profile payload ({ id, display_name, ... }). */
function parseProfilePayload(body: {
  id?: string;
  username?: string;
  display_name?: string;
  avatar_url?: string | null;
  bio?: string;
  support_link?: string;
  created_at?: string | null;
}): ProfileFields {
  return {
    id: body.id ?? "",
    username: body.username?.trim() ?? "",
    displayName: body.display_name?.trim() || "Author",
    avatarUrl: body.avatar_url ?? null,
    bio: body.bio ?? "",
    supportLink: body.support_link ?? "",
    createdAt: body.created_at ?? null
  };
}

export interface CreateGenerationJobInput {
  idea: string;
  genre?: string;
  tone?: string;
  length?: "short" | "medium" | "long";
  styleNotes?: string;
  /** Author note on the visual art style for all images ("аниме", "watercolour"). */
  artStyle?: string;
  /** The novel's language code — with `detectLanguage`, only the fallback hint (the UI language). */
  language?: string;
  /** Let the model detect the language from the idea (an API that predates it ignores the flag). */
  detectLanguage?: boolean;
  /** Render real images during generation. Defaults to true server-side. */
  generateImages?: boolean;
  /**
   * The paid emotion-sprite add-on (`novel_emotions`): up to 3 expressions per
   * character. Ignored — and not charged — when `generateImages` is false.
   */
  emotions?: boolean;
}

type Fetcher = typeof fetch;

const defaultApiBaseUrl = "http://localhost:4000";

/**
 * A typed API failure. `status` is the HTTP status and `code` the server's
 * machine-readable `error` field (e.g. "insufficient_credits", "not_in_beta",
 * "budget_exceeded"), so callers can branch on the reason instead of parsing a
 * message. Extends Error, so existing `instanceof Error` / `.message` handling
 * keeps working.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string | undefined,
    message: string,
    /** Seconds to wait before retrying, parsed from the Retry-After header on 429s. */
    public readonly retryAfterSeconds?: number
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Parses a Retry-After header — the API sends it as integer seconds — into a
 * number. Returns undefined when the header is absent or not a valid count, so
 * callers fall back to their own default backoff.
 */
function parseRetryAfterSeconds(value: string | null): number | undefined {
  if (!value) return undefined;
  const seconds = Number(value);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : undefined;
}

/**
 * Throws a typed {@link ApiError} when `response` is not ok, pulling the
 * server's `error` code and `message` (and any validationErrors) out of the JSON
 * body. `fallbackMessage` is used when the body carries no message.
 */
export async function assertOk(response: Response, fallbackMessage: string): Promise<void> {
  if (response.ok) return;
  const body = (await response.json().catch(() => null)) as {
    error?: string;
    message?: string;
    required_cents?: number;
    balance_cents?: number;
    validationErrors?: Array<{ code: string; message: string }>;
  } | null;
  const validationDetails = body?.validationErrors
    ?.map((error) => `${error.code}: ${error.message}`)
    .join("; ");
  throw new ApiError(
    response.status,
    body?.error,
    validationDetails || body?.message || insufficientCreditsMessage(body) || fallbackMessage,
    response.status === 429 ? parseRetryAfterSeconds(response.headers.get("Retry-After")) : undefined
  );
}

const creditsLabel = (credits: number): string => `${credits.toLocaleString()} credits`;

/**
 * A human message for the one credit denial the server sends without a `message`:
 * the 402 insufficient_credits body carries required_cents / balance_cents only,
 * so synthesize a clear sentence here rather than let {@link assertOk} fall back
 * to a route-generic "… failed" string. Returns undefined for any other error,
 * so budget_exceeded (which ships its own message) and everything else are
 * unaffected.
 */
function insufficientCreditsMessage(
  body: { error?: string; required_cents?: number; balance_cents?: number } | null
): string | undefined {
  if (body?.error !== "insufficient_credits") return undefined;
  return typeof body.required_cents === "number" && typeof body.balance_cents === "number"
    ? `Not enough credits — this costs ${creditsLabel(body.required_cents)} and your balance is ${creditsLabel(body.balance_cents)}. Buy more on the Pricing page.`
    : "Not enough credits for this action. Buy more on the Pricing page.";
}

/** Why the API refused a signed-in account outright (403 on any route). */
export type AccountBlockReason = "not_in_beta" | "account_suspended";

/**
 * Global hook invoked when any authenticated request is rejected with 403
 * not_in_beta (the account is not on the closed-beta allowlist) or 403
 * account_suspended (the account was suspended). The app registers a handler to
 * surface the matching screen. Set to null to clear it.
 */
let notInBetaHandler: ((reason: AccountBlockReason) => void) | null = null;
export function setNotInBetaHandler(handler: ((reason: AccountBlockReason) => void) | null): void {
  notInBetaHandler = handler;
}

/**
 * Default fetcher that attaches the Supabase access token (when logged in) as a
 * Bearer header so the API can identify the owner. Tests pass their own fetcher
 * and are unaffected. A 403 not_in_beta anywhere trips the global handler so the
 * whole app can drop to the beta gate, not just the route that happened to call.
 */
const authedFetch: Fetcher = async (input, init = {}) => {
  const token = await getAccessToken();
  const mutatesContent = init.method !== undefined && init.method !== "GET";
  if (mutatesContent) invalidateReadMemory();
  // A signed-out read the page's inline script already started (see
  // lib/prefetchRoutes.ts) is adopted instead of being asked for again.
  const prefetched = !token && !mutatesContent && typeof input === "string" ? await takePrefetched(input, init.signal) : null;
  let response: Response;
  try {
    response = token
      ? await fetch(input, { ...init, headers: withBearer(init.headers, token) })
      : prefetched ?? (await fetch(input, init));
  } catch (error) {
    // Unreachable API (or a deliberate abort): let the maintenance watch decide.
    if (!(error instanceof DOMException && error.name === "AbortError")) reportApiTrouble();
    throw error;
  }
  // A restarting API (a redeploy) answers 502/503/504 from the host.
  if (isRestartStatus(response.status)) reportApiTrouble();
  if (mutatesContent) invalidateReadMemory();
  // A denial for a SIGNED-IN caller means their session or access changed, so
  // cached private reads may be stale. An anonymous 401/403 (a signed-out
  // visitor touching an account-only action, a reader signal refused for its
  // Origin) says nothing about the cached public data and must not wipe it —
  // unless it is the site's own access gate closing.
  if ((response.status === 401 || response.status === 403) && token) clearApiCache();
  if (response.status === 403 && (notInBetaHandler || !token)) {
    // Peek at a clone so the caller still gets an unconsumed body.
    try {
      const body = (await response.clone().json()) as { error?: string } | null;
      if (body?.error === "not_in_beta" || body?.error === "account_suspended") {
        if (!token) clearApiCache();
        notInBetaHandler?.(body.error);
      }
    } catch {
      // Not JSON / already consumed — nothing to branch on.
    }
  }
  return response;
};

function withBearer(headers: HeadersInit | undefined, token: string): Headers {
  const merged = new Headers(headers);
  merged.set("Authorization", `Bearer ${token}`);
  return merged;
}

/** A credit pack on the pricing page. */
export interface CreditPack {
  id: string;
  /** List price in the pricing currency's minor units (USD cents), before tax. */
  priceCents: number;
  /** Credits granted on purchase. */
  creditsCents: number;
  /** Whether it can be bought in-app right now (Paddle configured + priced). */
  purchasable: boolean;
}

/** Prices (in credits) per billable operation, the signup grant, and the packs on sale. */
export interface CreditPricesResult {
  prices: Record<string, number>;
  signupGrantCents: number;
  /** External buy-credits URL (pre-Paddle concierge link), or null. */
  purchaseUrl: string | null;
  /** Pricing currency of the packs (ISO code, "USD"). */
  currency: string;
  /** Packs on sale, in display order. */
  packs: CreditPack[];
  /** True when the in-app Paddle checkout can be started. */
  checkoutEnabled: boolean;
  /** Paddle environment the checkout's transactions live in. */
  paddleEnvironment: "sandbox" | "production";
}

/** Current credit balance in cents for the signed-in user (401 when anonymous). */
export async function loadCreditBalance(
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<number> {
  const response = await fetcher(`${apiBaseUrl}/api/credits/balance`);
  await assertOk(response, "Could not load your credit balance.");
  const body = (await response.json()) as { balance_cents: number };
  return body.balance_cents;
}

/** Operation prices + signup grant. Static per deployment; loaded once. */
export async function loadCreditPrices(
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<CreditPricesResult> {
  const response = await fetcher(`${apiBaseUrl}/api/credits/prices`);
  await assertOk(response, "Could not load credit prices.");
  const body = (await response.json()) as {
    prices: Record<string, number>;
    signup_grant_cents: number;
    purchase_url?: string | null;
    currency?: string;
    checkout_enabled?: boolean;
    paddle_environment?: string;
    packs?: Array<{ id: string; price_cents: number; credits_cents: number; purchasable?: boolean }>;
  };
  return {
    prices: body.prices,
    signupGrantCents: body.signup_grant_cents,
    purchaseUrl: body.purchase_url ?? null,
    currency: body.currency ?? "USD",
    packs: (body.packs ?? []).map((pack) => ({
      id: pack.id,
      priceCents: pack.price_cents,
      creditsCents: pack.credits_cents,
      purchasable: pack.purchasable === true
    })),
    checkoutEnabled: body.checkout_enabled === true,
    paddleEnvironment: body.paddle_environment === "production" ? "production" : "sandbox"
  };
}

/**
 * Starts a Paddle checkout for one credit pack (signed-in only). Returns the
 * Paddle transaction id to open in CheckoutModal (Paddle's inline checkout); the
 * credits are added by the server when Paddle confirms the payment (webhook).
 */
export async function createCheckout(
  packId: string,
  /**
   * The buyer's checkout consents: 18+ and acceptance of the Terms and Refund
   * Policy, and the express request to start the service immediately (EU/UK
   * withdrawal rules). The API refuses the checkout unless both are true.
   */
  consents: { termsAccepted: boolean; immediateStart: boolean },
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<{ transactionId: string }> {
  const response = await fetcher(`${apiBaseUrl}/api/billing/checkout`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      pack_id: packId,
      age_confirmed: consents.termsAccepted,
      terms_accepted: consents.termsAccepted,
      immediate_start: consents.immediateStart
    })
  });
  await assertOk(response, "Could not start the checkout.");
  const body = (await response.json()) as { transaction_id: string };
  return { transactionId: body.transaction_id };
}

export interface MeResult {
  userId: string;
  email: string | null;
  username: string;
  displayName: string;
  avatarUrl: string | null;
  bio: string;
  createdAt: string | null;
  /**
   * False while the account still has to pick its username (a first Google /
   * Discord sign-in): the app shows "Finish signing up" until it does.
   */
  usernameConfirmed: boolean;
  isAdmin: boolean;
  balanceCents: number;
}

/** Identity + admin flag + balance + own profile (401 when anonymous). */
export async function loadMe(
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<MeResult> {
  const response = await fetcher(`${apiBaseUrl}/api/me`);
  await assertOk(response, "Could not load your account.");
  const body = (await response.json()) as {
    user_id: string;
    email: string | null;
    username?: string;
    display_name?: string;
    avatar_url?: string | null;
    bio?: string;
    created_at?: string | null;
    username_confirmed?: boolean;
    is_admin: boolean;
    balance_cents: number;
  };
  const profile = parseProfilePayload(body);
  return {
    userId: body.user_id,
    email: body.email,
    username: profile.username,
    displayName: profile.displayName,
    avatarUrl: profile.avatarUrl,
    bio: profile.bio,
    createdAt: profile.createdAt,
    // An older API sends no flag: every account counts as confirmed.
    usernameConfirmed: body.username_confirmed !== false,
    isAdmin: body.is_admin,
    balanceCents: body.balance_cents
  };
}

/**
 * Loads a public author profile — profile fields + the author's published
 * novels. Falls back to an empty profile on any failure so the page renders.
 */
export async function loadAuthorProfile(
  id: string,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<AuthorProfileResult> {
  const empty: AuthorProfileResult = {
    id,
    username: "",
    displayName: "Author",
    avatarUrl: null,
    bio: "",
    supportLink: "",
    createdAt: null,
    novels: []
  };
  try {
    const response = await fetcher(`${apiBaseUrl}/api/profiles/${encodeURIComponent(id)}`);
    if (response.status === 404) return { ...empty, notFound: true };
    if (!response.ok) return { ...empty, unavailable: isTransientServerFailure(response.status) };
    const body = (await response.json()) as Parameters<typeof parseProfilePayload>[0] & {
      novels?: NovelSummary[];
    };
    return { ...parseProfilePayload({ ...body, id: body.id ?? id }), novels: body.novels ?? [] };
  } catch {
    return { ...empty, unavailable: true };
  }
}

/** Why a username can't be used, or null when it is free. */
export type UsernameUnavailableReason = "invalid" | "reserved" | "taken" | "rejected";

/**
 * Asks whether `username` is free (the sign-up form). Throws when the API can't
 * answer, so the form can say "couldn't check" instead of guessing.
 */
export async function checkUsername(
  username: string,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<{ available: boolean; reason: UsernameUnavailableReason | null }> {
  const response = await fetcher(`${apiBaseUrl}/api/usernames/${encodeURIComponent(username)}`);
  await assertOk(response, "Could not check the username.");
  const body = (await response.json()) as { available?: boolean; reason?: UsernameUnavailableReason | null };
  return { available: body.available === true, reason: body.reason ?? null };
}

/**
 * Signs in with a username: the API finds the account and signs in to Supabase
 * for us (the username's email never reaches the browser) and returns the
 * session tokens for the auth client. Throws with the API's message on refusal.
 */
export async function signInWithUsername(
  username: string,
  password: string,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<{ accessToken: string; refreshToken: string }> {
  const response = await fetcher(`${apiBaseUrl}/api/auth/username-login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password })
  });
  await assertOk(response, "Authentication failed.");
  const body = (await response.json()) as { access_token?: string; refresh_token?: string };
  if (!body.access_token || !body.refresh_token) throw new Error("Authentication failed.");
  return { accessToken: body.access_token, refreshToken: body.refresh_token };
}

/**
 * Claims the signed-in account's username — once, for an account that never
 * chose one (a first Google / Discord sign-in), with the 18+ and Terms/Privacy
 * consents. Throws an ApiError whose `code` says why not: invalid_username,
 * username_reserved, username_rejected, username_taken (409),
 * username_already_set (403), consent_required, rate_limited.
 */
export async function claimUsername(
  username: string,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<void> {
  const response = await fetcher(`${apiBaseUrl}/api/profile/username`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, age_confirmed: true, terms_accepted: true })
  });
  await assertOk(response, "Could not save your username.");
  // Bylines on the account's novels (if any) carry the username.
  invalidateListCaches();
}

/** Sets the signed-in author's display name and/or bio. Returns the profile. */
export async function updateProfile(
  patch: { displayName?: string; bio?: string; supportLink?: string },
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<ProfileFields> {
  const payload: { display_name?: string; bio?: string; support_link?: string } = {};
  if (patch.displayName !== undefined) payload.display_name = patch.displayName;
  if (patch.bio !== undefined) payload.bio = patch.bio;
  if (patch.supportLink !== undefined) payload.support_link = patch.supportLink;
  const response = await fetcher(`${apiBaseUrl}/api/profile`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload)
  });
  await assertOk(response, "Could not save your profile.");
  // Author bylines across the catalog now change — drop the list caches so the
  // next read re-fetches the updated author rather than serving a stale copy.
  invalidateListCaches();
  return parseProfilePayload((await response.json()) as Parameters<typeof parseProfilePayload>[0]);
}

/** Uploads a new avatar (a data URL). Returns the updated profile. */
export async function uploadAvatar(
  dataUrl: string,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<ProfileFields> {
  const response = await fetcher(`${apiBaseUrl}/api/profile/avatar`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ image: dataUrl })
  });
  await assertOk(response, "Could not upload your avatar.");
  invalidateListCaches();
  return parseProfilePayload((await response.json()) as Parameters<typeof parseProfilePayload>[0]);
}

/** Removes the signed-in author's avatar. Returns the updated profile. */
export async function removeAvatar(
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<ProfileFields> {
  const response = await fetcher(`${apiBaseUrl}/api/profile/avatar`, { method: "DELETE" });
  await assertOk(response, "Could not remove your avatar.");
  invalidateListCaches();
  return parseProfilePayload((await response.json()) as Parameters<typeof parseProfilePayload>[0]);
}

/**
 * Generates a new avatar via the image provider from a short description. This
 * is a PAID operation (charges credits server-side). Returns the updated
 * profile; the caller should refresh the account balance afterwards.
 */
export async function generateAvatar(
  prompt: string,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<ProfileFields> {
  const response = await fetcher(`${apiBaseUrl}/api/profile/avatar/generate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ prompt })
  });
  await assertOk(response, "Could not generate your avatar.");
  invalidateListCaches();
  return parseProfilePayload((await response.json()) as Parameters<typeof parseProfilePayload>[0]);
}

/** Longest seed the idea route accepts (mirrors MAX_IDEA_SEED_LENGTH in the API). */
export const MAX_IDEA_SEED_LENGTH = 400;

/**
 * "Come up with an idea" on the Create page: one PAID LLM call that turns the
 * author's seed (a word, a rough draft, or nothing) into an original premise in
 * `language`. Charges credits server-side; the caller should refresh the balance.
 */
export async function suggestIdea(
  input: { seed: string; genre?: string; tone?: string; language: string },
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<{ idea: string; balanceCents: number | null }> {
  const response = await fetcher(`${apiBaseUrl}/api/ideas/suggest`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...input, seed: input.seed.trim().slice(0, MAX_IDEA_SEED_LENGTH) })
  });
  await assertOk(response, "Could not come up with an idea.");
  const body = (await response.json()) as { idea?: unknown; balance_cents?: unknown };
  if (typeof body.idea !== "string" || !body.idea.trim()) {
    throw new Error("Could not come up with an idea.");
  }
  return {
    idea: body.idea.trim(),
    balanceCents: typeof body.balance_cents === "number" ? body.balance_cents : null
  };
}

// ─── Admin dashboard ──────────────────────────────────────────────────────────

export interface AdminActivityRow {
  userId: string;
  email: string;
  novels: number;
  generations: number;
  creditsGrantedCents: number;
  creditsSpentCents: number;
  balanceCents: number;
  lastActive: string | null;
  /** The account's handle, sign-up and latest sign-in (absent from an older API). */
  username?: string | null;
  createdAt?: string | null;
  lastSignInAt?: string | null;
  /** True when the account is suspended (absent from an older API). */
  suspended?: boolean;
}

export interface AdminMarginRow {
  key: string;
  chargedCents: number;
  realCostCents: number;
  marginCents: number;
  opCount: number;
}

/** One operation's margin on one UTC day (`key` = the operation). */
export interface AdminMarginDayOperationRow extends AdminMarginRow {
  day: string;
}

export interface AdminMargin {
  total: Omit<AdminMarginRow, "key">;
  byOperation: AdminMarginRow[];
  byUser: AdminMarginRow[];
  /** Per UTC day / ISO week (keyed by its Monday) / month, newest first. */
  byDay?: AdminMarginRow[];
  byWeek?: AdminMarginRow[];
  byMonth?: AdminMarginRow[];
  /**
   * Per UTC day and operation (`key` = the operation) — what a period row
   * drills into; a week or month sums its days. Absent from an older API.
   */
  byDayOperation?: AdminMarginDayOperationRow[];
  /**
   * UTC days (YYYY-MM-DD) hidden from every figure above, newest first, and
   * their own totals (only days with events). Absent from an older API.
   */
  hiddenDays?: string[];
  hiddenByDay?: AdminMarginRow[];
  /**
   * When the owner last cleared the margin data (ISO), or null — every figure
   * above counts only later operations. `cleared` totals the earlier ones
   * (nothing is deleted). Absent from an older API.
   */
  resetAt?: string | null;
  cleared?: Omit<AdminMarginRow, "key">;
}

/**
 * Suspends an account by email (it can no longer use the site) or lifts the
 * suspension. The route lives under /invites for the closed-beta allowlist,
 * where suspensions are stored.
 */
export async function setAdminInviteSuspended(
  email: string,
  suspended: boolean,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<void> {
  const response = await fetcher(
    `${apiBaseUrl}/api/admin/invites/${encodeURIComponent(email)}/suspend`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ suspended })
    }
  );
  await assertOk(response, "Could not update the account.");
}

export async function loadAdminActivity(
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<AdminActivityRow[]> {
  const response = await fetcher(`${apiBaseUrl}/api/admin/activity`);
  await assertOk(response, "Could not load activity.");
  return ((await response.json()) as { users?: AdminActivityRow[] }).users ?? [];
}

export async function loadAdminMargin(
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<AdminMargin> {
  const response = await fetcher(`${apiBaseUrl}/api/admin/margin`);
  await assertOk(response, "Could not load margin.");
  return (await response.json()) as AdminMargin;
}

/** Days per request — the API's cap on one hide/restore call. */
const MARGIN_HIDDEN_DAYS_PER_REQUEST = 400;

/**
 * Hides (or restores) UTC days in the margin report and returns the refreshed
 * report. Nothing is deleted server-side; a longer list is sent in chunks.
 */
export async function setAdminMarginDaysHidden(
  days: string[],
  hidden: boolean,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<AdminMargin> {
  let report: AdminMargin | null = null;
  for (let start = 0; start < days.length; start += MARGIN_HIDDEN_DAYS_PER_REQUEST) {
    const response = await fetcher(`${apiBaseUrl}/api/admin/margin/hidden-days`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ days: days.slice(start, start + MARGIN_HIDDEN_DAYS_PER_REQUEST), hidden })
    });
    await assertOk(response, hidden ? "Could not hide the days." : "Could not restore the days.");
    report = (await response.json()) as AdminMargin;
  }
  if (!report) throw new Error("No days were given.");
  return report;
}

/**
 * "Clear margin data": the report starts counting from now (nothing is
 * deleted). `undo` removes the latest clear instead. Returns the new report.
 */
export async function resetAdminMargin(
  undo = false,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<AdminMargin> {
  const response = await fetcher(`${apiBaseUrl}/api/admin/margin/reset`, { method: undo ? "DELETE" : "POST" });
  await assertOk(response, undo ? "Could not bring the margin data back." : "Could not clear the margin data.");
  return (await response.json()) as AdminMargin;
}

export interface AdminBudget {
  dailySpendCents: number;
  dailyBudgetCents: number;
  /** True when the daily budget kill-switch has tripped — the beta is paused. */
  tripped: boolean;
}

export async function loadAdminBudget(
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<AdminBudget> {
  const response = await fetcher(`${apiBaseUrl}/api/admin/budget`);
  await assertOk(response, "Could not load the budget.");
  const body = (await response.json()) as {
    daily_spend_cents?: number;
    daily_budget_cents?: number;
    tripped?: boolean;
  };
  return {
    dailySpendCents: body.daily_spend_cents ?? 0,
    dailyBudgetCents: body.daily_budget_cents ?? 0,
    tripped: body.tripped ?? false
  };
}

/** One provider's manually-recorded account balance (fal.ai / Anthropic). */
export interface AdminProviderBalance {
  provider: string;
  balanceCents: number;
  note: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

export async function loadAdminProviderBilling(
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<AdminProviderBalance[]> {
  const response = await fetcher(`${apiBaseUrl}/api/admin/provider-billing`);
  await assertOk(response, "Could not load provider balances.");
  return ((await response.json()) as { providers?: AdminProviderBalance[] }).providers ?? [];
}

/** Records one provider's current balance (dollars → cents at the call site). */
export async function saveAdminProviderBalance(
  provider: string,
  balanceCents: number,
  note: string | undefined,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<AdminProviderBalance> {
  const response = await fetcher(`${apiBaseUrl}/api/admin/provider-billing`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ provider, balance_cents: balanceCents, note })
  });
  await assertOk(response, "Could not save the balance.");
  return (await response.json()) as AdminProviderBalance;
}

/** Reads the flat per-operation surcharge (owner commission) in cents. */
export async function loadAdminMarkup(
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<number> {
  const response = await fetcher(`${apiBaseUrl}/api/admin/markup`);
  await assertOk(response, "Could not load the markup.");
  return ((await response.json()) as { markup_cents?: number }).markup_cents ?? 0;
}

/** Sets the flat per-operation surcharge (owner commission) in cents. */
export async function saveAdminMarkup(
  markupCents: number,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<number> {
  const response = await fetcher(`${apiBaseUrl}/api/admin/markup`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ markup_cents: markupCents })
  });
  await assertOk(response, "Could not save the markup.");
  return ((await response.json()) as { markup_cents?: number }).markup_cents ?? 0;
}

/** One deduped captured crash, as the triage agent and admin view see it. */
export interface AdminErrorEvent {
  id: number;
  source: string;
  kind: string | null;
  message: string;
  route: string | null;
  release_sha: string | null;
  count: number;
  status: string;
  triage_notes: string | null;
  pr_url: string | null;
  last_seen: string;
}

export async function loadAdminErrors(
  status?: string,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<AdminErrorEvent[]> {
  const query = status ? `?status=${encodeURIComponent(status)}` : "";
  const response = await fetcher(`${apiBaseUrl}/api/admin/errors${query}`);
  await assertOk(response, "Could not load errors.");
  return ((await response.json()) as { errors?: AdminErrorEvent[] }).errors ?? [];
}

/** Moves one captured error through its lifecycle (e.g. ignore, resolve). */
export async function updateAdminErrorStatus(
  id: number,
  status: string,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<void> {
  const response = await fetcher(`${apiBaseUrl}/api/admin/errors/${id}/status`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status })
  });
  await assertOk(response, "Could not update the error.");
}

export interface AdminUserNovel {
  id: string;
  title: string;
  visibility: string;
  updated_at: string;
  idea: string | null;
}

export interface AdminUserCost {
  operation: string;
  ref_id: string | null;
  charged_cents: number | null;
  real_cost_cents: number;
  created_at: string;
}

export interface AdminUserDetail {
  userId: string;
  email: string;
  balanceCents: number;
  grantedCents: number;
  spentCents: number;
  novels: AdminUserNovel[];
  operations: AdminUserCost[];
  /** True when the account is suspended (absent from an older API). */
  suspended?: boolean;
}

export async function loadAdminUserDetail(
  userId: string,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<AdminUserDetail> {
  const response = await fetcher(`${apiBaseUrl}/api/admin/users/${encodeURIComponent(userId)}`);
  await assertOk(response, "Could not load the user's activity.");
  return (await response.json()) as AdminUserDetail;
}

/**
 * Tops up (positive) or deducts from (negative) a user's credit balance by
 * `deltaCents`. Returns the resulting balance in cents.
 */
export async function adjustAdminUserBalance(
  userId: string,
  deltaCents: number,
  reason: string | undefined = undefined,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<number> {
  const response = await fetcher(
    `${apiBaseUrl}/api/admin/users/${encodeURIComponent(userId)}/credits`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ delta_cents: deltaCents, reason })
    }
  );
  await assertOk(response, "Could not update the user's balance.");
  return ((await response.json()) as { balance_cents: number }).balance_cents;
}

// ─── Beta support: feedback + password reset ────────────────────────────────

/** Submits in-app feedback (bug / idea / other) from a signed-in tester. */
export async function sendFeedback(
  input: {
    kind: string;
    message: string;
    url?: string;
    /** Optional contact (email or Discord handle) for a reply; rides to Discord. */
    contact?: string;
    context?: import("./lib/clientContext.js").FeedbackClientContext;
    /** Up to 10 page screenshots as data URLs; stored and mirrored to Discord. */
    screenshots?: string[];
  },
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<void> {
  const response = await fetcher(`${apiBaseUrl}/api/feedback`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input)
  });
  await assertOk(response, "Could not send your feedback.");
}

/**
 * Files a password-reset request from the sign-in page. Unauthenticated; the
 * owner resets the password in the admin dashboard (invite email is disabled).
 * The server always answers 204, so this never reveals whether an email exists.
 */
/** Public site configuration, readable before sign-in. */
export interface SiteConfig {
  /** Public storefront with self-serve signup (false = closed, invite-only beta). */
  openSignup: boolean;
  /** "Continue with Google / Discord" buttons to show; absent or empty = none. */
  oauthProviders?: OAuthProvider[];
}

/**
 * Loads the public site configuration. An unreachable or older API resolves to
 * the invite-only default, which only ever hides the signup form — it never
 * exposes a surface the server would refuse.
 */
const SITE_CONFIG_KEY = "lorevok:site-config";
// Kept apart from the mode (which older builds stored as a bare "open"/"closed").
const OAUTH_PROVIDERS_KEY = "lorevok:oauth-providers";

/**
 * The last /api/site-config answer this browser saw, or null on a first visit.
 * Lets the sign-in screen render in the right mode immediately (open sign-up vs
 * invite-only beta) instead of flashing the wrong copy while the API answers.
 */
export function cachedSiteConfig(): SiteConfig | null {
  const raw = safeLocalStorageGet(SITE_CONFIG_KEY);
  if (raw !== "open" && raw !== "closed") return null;
  return withOAuthProviders(raw === "open", parseOAuthProviders(safeLocalStorageGet(OAUTH_PROVIDERS_KEY) ?? ""));
}

/** The site config, carrying `oauthProviders` only when there are any (the usual answer has none). */
function withOAuthProviders(openSignup: boolean, oauthProviders: OAuthProvider[]): SiteConfig {
  return oauthProviders.length > 0 ? { openSignup, oauthProviders } : { openSignup };
}

/**
 * The site mode from the API, or null when the API could not answer (down,
 * redeploying) — the caller keeps what it knew rather than assume a closed beta.
 */
export async function loadSiteConfig(
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<SiteConfig | null> {
  try {
    const response = await fetcher(`${apiBaseUrl}/api/site-config`);
    if (!response.ok) return null;
    const body = (await response.json()) as { open_signup?: unknown; oauth_providers?: unknown };
    const openSignup = body.open_signup === true;
    // Only names this build knows get a button; an older API sends none.
    const oauthProviders = parseOAuthProviders(body.oauth_providers);
    // Only a real answer is remembered — a network failure must not pin the mode.
    safeLocalStorageSet(SITE_CONFIG_KEY, openSignup ? "open" : "closed");
    safeLocalStorageSet(OAUTH_PROVIDERS_KEY, oauthProviders.join(","));
    return withOAuthProviders(openSignup, oauthProviders);
  } catch {
    return null;
  }
}

export async function requestPasswordReset(
  email: string,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<void> {
  const response = await fetcher(`${apiBaseUrl}/api/auth/reset-request`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email })
  });
  await assertOk(response, "Could not send your request.");
}

export interface AdminFeedback {
  id: number;
  user_id: string | null;
  email: string | null;
  kind: string;
  message: string;
  url: string | null;
  status: string;
  created_at: string;
}

export async function loadAdminFeedback(
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<AdminFeedback[]> {
  const response = await fetcher(`${apiBaseUrl}/api/admin/feedback`);
  await assertOk(response, "Could not load feedback.");
  return ((await response.json()) as { feedback?: AdminFeedback[] }).feedback ?? [];
}

/** Client debug context a reporter's browser attached to a feedback item. */
export interface AdminFeedbackContext {
  releaseSha?: string;
  userAgent?: string;
  viewport?: string;
  locale?: string;
  novelTitle?: string;
  sceneId?: string;
  sceneTitle?: string;
  editorTab?: string;
}

/** One feedback item with the heavy detail the list omits. */
export interface AdminFeedbackDetail extends AdminFeedback {
  contact: string | null;
  context: AdminFeedbackContext | null;
  /** Page screenshots as data URLs (0-10). */
  screenshots: string[];
}

export async function loadAdminFeedbackDetail(
  id: number,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<AdminFeedbackDetail> {
  const response = await fetcher(`${apiBaseUrl}/api/admin/feedback/${id}`);
  await assertOk(response, "Could not load feedback.");
  return (await response.json()) as AdminFeedbackDetail;
}

export type ReportTargetType = "novel" | "comment" | "profile";
export type ReportReason =
  | "sexual"
  | "minors"
  | "violence"
  | "hate"
  | "real_person"
  | "copyright"
  | "spam"
  | "other";

/** Files a content report (open to signed-out visitors; rate-limited server-side). */
export async function submitReport(
  report: {
    targetType: ReportTargetType;
    targetId: string;
    novelId?: string;
    reason: ReportReason;
    details?: string;
    contact?: string;
    reporterName?: string;
  },
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<void> {
  const response = await fetcher(`${apiBaseUrl}/api/reports`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      target_type: report.targetType,
      target_id: report.targetId,
      novel_id: report.novelId,
      reason: report.reason,
      details: report.details?.trim() || undefined,
      contact: report.contact?.trim() || undefined,
      reporter_name: report.reporterName?.trim() || undefined,
      // The reporter confirmed the report is accurate and made in good faith (EU DSA art. 16).
      good_faith: true
    })
  });
  await assertOk(response, "Could not send the report.");
}

/** Permanently deletes the signed-in account and everything it owns. */
export async function deleteAccount(fetcher: Fetcher = authedFetch, apiBaseUrl = getApiBaseUrl()): Promise<void> {
  const response = await fetcher(`${apiBaseUrl}/api/account`, { method: "DELETE" });
  await assertOk(response, "Could not delete the account.");
}

/** Admin takedown: turns a published novel back into its author's private draft. */
export async function adminUnpublishNovel(
  novelId: string,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<void> {
  const response = await fetcher(`${apiBaseUrl}/api/admin/novels/${encodeURIComponent(novelId)}/unpublish`, {
    method: "POST"
  });
  await assertOk(response, "Could not unpublish the novel.");
}

/** Admin takedown: removes a comment (soft delete). */
export async function adminDeleteComment(
  novelId: string,
  commentId: string,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<void> {
  const response = await fetcher(
    `${apiBaseUrl}/api/admin/novels/${encodeURIComponent(novelId)}/comments/${encodeURIComponent(commentId)}`,
    { method: "DELETE" }
  );
  await assertOk(response, "Could not remove the comment.");
}

export async function resolveAdminFeedback(
  id: number,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<void> {
  const response = await fetcher(`${apiBaseUrl}/api/admin/feedback/${id}/resolve`, {
    method: "POST"
  });
  await assertOk(response, "Could not update feedback.");
}

// A definitive server "no" — deleted, hidden, no longer ours, unaffordable, or
// outside the closed beta. A cached copy must not outlive any of these answers.
const DENIAL_STATUSES = new Set([401, 402, 403, 404, 410]);

// A cold-starting host can accept the connection and then sit silent for
// 30-60s. Without a bound, that hang pins the loading skeletons while a
// perfectly good cached copy sits unused — so cached LIST reads give up after
// this long and fall back to the cache. The server keeps waking up regardless;
// the next read gets fresh data.
const LIST_READ_TIMEOUT_MS = 12_000;
// Single-novel reads get a much longer leash: aborting a slow-but-successful
// first-time load would silently hand the player the demo novel, which is far
// worse than a longer wait. This bound only rescues genuinely hung requests.
const NOVEL_READ_TIMEOUT_MS = 60_000;

// Older browsers lack AbortSignal.timeout; running without a bound there is
// strictly better than the TypeError being swallowed as a "network failure"
// (which would short-circuit every read to the cache forever).
function readTimeoutSignal(timeoutMs: number): AbortSignal | undefined {
  return typeof AbortSignal.timeout === "function" ? AbortSignal.timeout(timeoutMs) : undefined;
}

// Server-side trouble (cold start, deploy, overload): the request never got a
// usable answer, so the last cached copy is still the best truth available.
function isTransientServerFailure(status: number): boolean {
  return status >= 500 || status === 429 || status === 408;
}

type CachedGetOutcome<TBody, TCached> =
  | { kind: "api"; body: TBody }
  | { kind: "cache"; cached: TCached }
  // `unavailable`: no usable answer (network, timeout, 5xx, a torn body) — as
  // opposed to a definitive refusal such as a 404.
  | { kind: "fallback"; unavailable: boolean };

/**
 * Shared read policy for the stale-while-revalidate endpoints: a successful
 * answer refreshes the cache; a network failure, a timed-out request, or a
 * transient 5xx serves the cached copy; a definitive denial evicts it.
 */
async function cachedGet<TBody, TCached>(options: {
  fetcher: Fetcher;
  url: string;
  timeoutMs: number;
  readCache: () => TCached | null;
  writeCache: (body: TBody) => void;
  evictCache: () => void;
}): Promise<CachedGetOutcome<TBody, TCached>> {
  const identity = apiIdentityRevision();
  const readCache = () => identity === apiIdentityRevision() ? options.readCache() : null;
  let response: Response;
  try {
    response = await options.fetcher(options.url, {
      signal: readTimeoutSignal(options.timeoutMs)
    });
  } catch {
    const cached = readCache();
    return cached !== null ? { kind: "cache", cached } : { kind: "fallback", unavailable: true };
  }

  try {
    if (identity !== apiIdentityRevision()) return { kind: "fallback", unavailable: true };
    if (!response.ok) {
      if (DENIAL_STATUSES.has(response.status)) {
        options.evictCache();
      } else if (isTransientServerFailure(response.status)) {
        const cached = readCache();
        if (cached !== null) return { kind: "cache", cached };
      }
      return { kind: "fallback", unavailable: isTransientServerFailure(response.status) };
    }
    const body = (await response.json()) as TBody;
    if (identity !== apiIdentityRevision()) return { kind: "fallback", unavailable: true };
    options.writeCache(body);
    return { kind: "api", body };
  } catch {
    // The timeout signal keeps governing body streaming, so a slow body (or a
    // malformed 200) lands here — the request got no usable answer, and the
    // cached copy is just as valid as it is for a failed connection.
    const cached = readCache();
    return cached !== null ? { kind: "cache", cached } : { kind: "fallback", unavailable: true };
  }
}

/** One "Load more" page of the catalog / My Novels grids. */
export const NOVEL_LIST_PAGE_SIZE = 24;
/** The API's per-request cap, used when a sort needs every remaining page. */
export const NOVEL_LIST_PAGE_SIZE_MAX = 100;

/**
 * A catalog page. With `filters` (language / genre) it reads only matching
 * novels — and never touches the offline copy, which holds the unfiltered first
 * page; an unanswered filtered read is an empty "fallback" (the demo novel
 * would pretend to match).
 */
export async function loadNovelCatalog(
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl(),
  offset?: number,
  limit = NOVEL_LIST_PAGE_SIZE,
  filters?: CatalogFilters
): Promise<NovelCatalogResult> {
  const filtered = filters !== undefined && hasCatalogFilters(filters);
  // lib/prefetchRoutes.ts builds the first page's URL the same way (its test pins it).
  const filterParams = filtered ? catalogFilterApiParams(filters) : "";
  const query = offset === undefined ? filterParams.replace(/^&/, "") : `limit=${limit}&offset=${offset}${filterParams}`;
  const result = await cachedGet<{ novels: NovelSummary[]; nextOffset?: number | null }, NovelSummary[]>({
    fetcher,
    url: `${apiBaseUrl}/api/novels${query ? `?${query}` : ""}`,
    timeoutMs: LIST_READ_TIMEOUT_MS,
    readCache: () => offset || filtered ? null : readApiCache<NovelSummary[]>(CATALOG_CACHE_KEY),
    writeCache: (body) => { if (!offset && !filtered) writeApiCache(CATALOG_CACHE_KEY, body.novels); },
    evictCache: () => { if (!filtered) removeApiCache(CATALOG_CACHE_KEY); }
  });
  if (result.kind === "api") {
    rememberNovelLinks(result.body.novels);
    return { novels: result.body.novels, source: "api", nextOffset: result.body.nextOffset };
  }
  if (result.kind === "cache") {
    rememberNovelLinks(result.cached);
    return { novels: result.cached, source: "cache" };
  }
  return { novels: filtered ? [] : [toNovelSummary(demoNovel)], source: "fallback" };
}

/** What the catalog's filter bar offers: languages and genres of public novels, with counts. */
export interface CatalogFacets {
  languages: Array<{ code: string; count: number }>;
  genres: Array<{ name: string; count: number }>;
}

/** How long a tab reuses the facets it read (the API caches them as long). */
const CATALOG_FACETS_MEMO_MS = 5 * 60_000;
let catalogFacetsMemo: {
  revision: number;
  at: number;
  facets: Promise<CatalogFacets | null>;
  /** The answer, once it is in (a failed read leaves the memo altogether). */
  value?: CatalogFacets;
} | null = null;

/**
 * The facets a tab already holds, straight away — for the filter bar's first
 * paint, so it can show them (or hold their space) instead of popping in a
 * frame later. Undefined when none are held, still loading, or out of date.
 */
export function peekCatalogFacets(now = Date.now()): CatalogFacets | undefined {
  const memo = catalogFacetsMemo;
  if (!memo || memo.revision !== apiCacheRevision() || now - memo.at >= CATALOG_FACETS_MEMO_MS) return undefined;
  return memo.value;
}

/**
 * GET /api/catalog/facets, remembered for a few minutes so returning to the
 * catalog does not ask again — until a content write (or an identity change)
 * bumps the read-memory revision. Null when the API could not answer (the
 * filter bar then offers only the filters already chosen).
 */
export function loadCatalogFacets(
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl(),
  now = Date.now()
): Promise<CatalogFacets | null> {
  const revision = apiCacheRevision();
  const memo = catalogFacetsMemo;
  if (memo && memo.revision === revision && now - memo.at < CATALOG_FACETS_MEMO_MS) return memo.facets;
  const facets = (async (): Promise<CatalogFacets | null> => {
    try {
      const response = await fetcher(`${apiBaseUrl}/api/catalog/facets`, { signal: readTimeoutSignal(LIST_READ_TIMEOUT_MS) });
      if (!response.ok) return null;
      const body = (await response.json()) as Partial<CatalogFacets>;
      return {
        languages: Array.isArray(body.languages)
          ? body.languages.filter((entry) => typeof entry?.code === "string" && typeof entry.count === "number")
          : [],
        genres: Array.isArray(body.genres)
          ? body.genres.filter((entry) => typeof entry?.name === "string" && typeof entry.count === "number")
          : []
      };
    } catch {
      return null;
    }
  })();
  const entry: NonNullable<typeof catalogFacetsMemo> = { revision, at: now, facets };
  catalogFacetsMemo = entry;
  // A failed read is not remembered: the next catalog visit asks again.
  void facets.then((result) => {
    if (result === null) {
      if (catalogFacetsMemo === entry) catalogFacetsMemo = null;
    } else {
      entry.value = result;
    }
  });
  return facets;
}

export async function loadMyNovels(
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl(),
  offset?: number,
  limit = NOVEL_LIST_PAGE_SIZE
): Promise<NovelCatalogResult> {
  const ownerId = isAuthEnabled ? "me" : localMvpOwnerId;
  const cacheKey = myNovelsCacheKey(ownerId);
  const result = await cachedGet<{ novels: NovelSummary[]; nextOffset?: number | null }, NovelSummary[]>({
    fetcher,
    url: `${apiBaseUrl}/api/novels?ownerId=${encodeURIComponent(ownerId)}&includeDrafts=true${offset === undefined ? "" : `&limit=${limit}&offset=${offset}`}`,
    timeoutMs: LIST_READ_TIMEOUT_MS,
    readCache: () => offset ? null : readApiCache<NovelSummary[]>(cacheKey),
    writeCache: (body) => { if (!offset) writeApiCache(cacheKey, body.novels); },
    evictCache: () => removeApiCache(cacheKey)
  });
  if (result.kind === "api") {
    rememberNovelLinks(result.body.novels);
    return { novels: result.body.novels, source: "api", nextOffset: result.body.nextOffset };
  }
  if (result.kind === "cache") {
    rememberNovelLinks(result.cached);
    return { novels: result.cached, source: "cache" };
  }
  return { novels: [], source: "fallback" };
}

export const loadCatalogPage = (offset = 0, limit?: number, filters?: CatalogFilters): Promise<NovelCatalogResult> =>
  loadNovelCatalog(undefined, undefined, offset, limit, filters);
export const loadMyNovelsPage = (offset = 0, limit?: number): Promise<NovelCatalogResult> =>
  loadMyNovels(undefined, undefined, offset, limit);

interface CachedNovelEntry {
  novel: Novel;
  stats: NovelStats;
  /** Author display name, cached so an offline read still shows the byline. */
  ownerDisplayName?: string;
  /** Author username, cached with the byline for its link. */
  ownerUsername?: string;
  /** Author avatar URL, cached alongside the byline name. */
  ownerAvatarUrl?: string | null;
  /** The caller's like state. The cache is dropped whenever the account changes. */
  likedByMe?: boolean;
}

export async function loadNovel(
  id: string,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<NovelResult> {
  const result = await cachedGet<
    {
      novel: Novel;
      stats?: NovelStats;
      owner_display_name?: string;
      owner_username?: string;
      owner_avatar_url?: string | null;
      liked_by_me?: boolean;
    },
    CachedNovelEntry
  >({
    fetcher,
    url: `${apiBaseUrl}/api/novels/${encodeURIComponent(id)}`,
    timeoutMs: NOVEL_READ_TIMEOUT_MS,
    readCache: () => readNovelCache<CachedNovelEntry>(id),
    // Stats are cached alongside the novel so an offline read shows the
    // last-seen real counters instead of fabricated zeros.
    writeCache: (body) => {
      const entry: CachedNovelEntry = {
        novel: body.novel,
        stats: body.stats ?? EMPTY_STATS,
        ownerDisplayName: body.owner_display_name,
        ownerUsername: body.owner_username,
        ownerAvatarUrl: body.owner_avatar_url,
        likedByMe: body.liked_by_me === true
      };
      writeNovelCache(id, entry);
      // Read by a link key: pages read the novel by its id (loadNovelForReading
      // hands them this copy), so an offline read by id finds it too.
      if (body.novel?.id && body.novel.id !== id) writeNovelCache(body.novel.id, entry);
    },
    evictCache: () => removeNovelCache(id)
  });
  if (result.kind === "api") {
    rememberNovelLink(result.body.novel);
    return {
      novel: stripStateSystem(normalizeNovel(result.body.novel)),
      stats: result.body.stats ?? EMPTY_STATS,
      ownerDisplayName: result.body.owner_display_name,
      ownerUsername: result.body.owner_username,
      ownerAvatarUrl: result.body.owner_avatar_url,
      likedByMe: result.body.liked_by_me === true,
      source: "api"
    };
  }
  if (result.kind === "cache") {
    rememberNovelLink(result.cached.novel);
    return {
      novel: stripStateSystem(normalizeNovel(result.cached.novel)),
      stats: result.cached.stats ?? EMPTY_STATS,
      ownerDisplayName: result.cached.ownerDisplayName,
      ownerUsername: result.cached.ownerUsername,
      ownerAvatarUrl: result.cached.ownerAvatarUrl,
      likedByMe: result.cached.likedByMe === true,
      source: "cache"
    };
  }
  return {
    novel: stripStateSystem(demoNovel),
    stats: EMPTY_STATS,
    source: "fallback",
    ...(result.unavailable ? { unavailable: true } : {})
  };
}

// Reading-only reuse: editors and exports still call loadNovel for a fresh copy.
const readingCopies = new Map<string, { revision: number; expires: number; result: Promise<NovelResult> }>();
export async function loadNovelForReading(id: string): Promise<NovelResult> {
  const identity = apiIdentityRevision();
  const copyForCurrentIdentity = (result: NovelResult): NovelResult =>
    identity === apiIdentityRevision() ? structuredClone(result) : {
      novel: stripStateSystem(demoNovel), stats: { ...EMPTY_STATS }, source: "fallback", unavailable: true
    };
  const revision = apiCacheRevision();
  const existing = readingCopies.get(id);
  if (existing && existing.revision === revision && existing.expires > Date.now()) {
    return copyForCurrentIdentity(await existing.result);
  }
  // Remove obsolete entries and bound the retained full documents.
  for (const [key, entry] of readingCopies) {
    if (entry.revision !== revision || entry.expires <= Date.now()) readingCopies.delete(key);
  }
  if (readingCopies.size >= 10) readingCopies.delete(readingCopies.keys().next().value!);
  const entry = { revision, expires: Date.now() + 15_000, result: loadNovel(id) };
  readingCopies.set(id, entry);
  const result = await entry.result;
  if (result.source !== "api" || apiCacheRevision() !== revision) {
    if (readingCopies.get(id) === entry) readingCopies.delete(id);
  } else if (result.novel.id !== id && identity === apiIdentityRevision()) {
    // Read by a link key (/novel/<slug>-<code>): the page that opens next asks
    // by the real id, so hand it this copy instead of reading the novel again.
    readingCopies.set(result.novel.id, entry);
  }
  return copyForCurrentIdentity(result);
}

export async function recordNovelView(
  id: string,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<NovelStats> {
  const response = await fetcher(`${apiBaseUrl}/api/novels/${encodeURIComponent(id)}/views`, {
    method: "POST"
  });
  if (!response.ok) {
    throw new Error(`View request failed with ${response.status}.`);
  }
  const body = (await response.json()) as { stats: NovelStats };
  return body.stats;
}

/** A like / unlike as the server applied it: the counters and the account's like state. */
export interface LikeResult {
  stats: NovelStats;
  liked: boolean;
}

/** Likes the novel for the signed-in account. Idempotent: one like per account. */
export async function likeNovel(
  id: string,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<LikeResult> {
  const response = await fetcher(`${apiBaseUrl}/api/novels/${encodeURIComponent(id)}/likes`, {
    method: "POST"
  });
  if (!response.ok) {
    throw new Error(`Like request failed with ${response.status}.`);
  }
  const body = (await response.json()) as { stats: NovelStats; liked?: boolean };
  return { stats: body.stats, liked: body.liked ?? true };
}

/** Records that the reader reached an ending — the finisher gate for rating. */
export async function recordNovelCompletion(
  id: string,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<NovelStats> {
  const response = await fetcher(`${apiBaseUrl}/api/novels/${encodeURIComponent(id)}/complete`, {
    method: "POST"
  });
  if (!response.ok) {
    throw new Error(`Completion request failed with ${response.status}.`);
  }
  return ((await response.json()) as { stats: NovelStats }).stats;
}

/** Withdraws the signed-in account's like (a no-op if it had none). */
export async function unlikeNovel(
  id: string,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<LikeResult> {
  const response = await fetcher(`${apiBaseUrl}/api/novels/${encodeURIComponent(id)}/likes`, {
    method: "DELETE"
  });
  if (!response.ok) {
    throw new Error(`Unlike request failed with ${response.status}.`);
  }
  const body = (await response.json()) as { stats: NovelStats; liked?: boolean };
  return { stats: body.stats, liked: body.liked ?? false };
}

// ── Comments ────────────────────────────────────────────────────────────────

/** Public byline attached to a comment/notification by the API. */
export interface AuthorByline {
  /** Permanent handle, for the link to the author page. */
  username?: string;
  display_name: string;
  avatar_url: string | null;
}

/** A comment under a novel, with its author's byline resolved by the API. */
export interface NovelComment {
  id: string;
  novel_id: string;
  user_id: string;
  /** Null for a top-level comment; the parent's id for a reply. */
  parent_id: string | null;
  body: string;
  created_at: string;
  deleted: boolean;
  author: AuthorByline;
  /** How many users liked this comment. */
  likes: number;
  /** Whether the signed-in user has liked it. */
  liked_by_me: boolean;
}

/** One page of a novel's comment threads (see `loadComments`). */
export interface CommentPage {
  /** The page's top-level comments and all of their replies, oldest-first. */
  comments: NovelComment[];
  /** Pass back to `loadComments` for the next, older threads; null on the last page. */
  nextCursor: string | null;
  /** Live comments under the novel, both levels — first page only (absent from an older API). */
  total?: number;
}

/**
 * Loads a page of a novel's comment threads, newest threads first, each with
 * all of its replies. Without a cursor it is the first page — the URL a cold
 * load prefetches (lib/prefetchRoutes.ts), so it carries no query string.
 */
export async function loadComments(
  novelId: string,
  cursor: string | null = null,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<CommentPage> {
  const query = cursor === null ? "" : `?cursor=${encodeURIComponent(cursor)}`;
  const response = await fetcher(`${apiBaseUrl}/api/novels/${encodeURIComponent(novelId)}/comments${query}`);
  if (!response.ok) {
    throw new Error(`Comments request failed with ${response.status}.`);
  }
  const body = (await response.json()) as { comments: NovelComment[]; next_cursor?: string | null; total?: number };
  return {
    comments: body.comments,
    nextCursor: body.next_cursor ?? null,
    ...(typeof body.total === "number" ? { total: body.total } : {})
  };
}

/** Posts a comment (or a reply, when `parentId` is given). */
export async function addComment(
  novelId: string,
  body: string,
  parentId: string | null = null,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<NovelComment> {
  const response = await fetcher(`${apiBaseUrl}/api/novels/${encodeURIComponent(novelId)}/comments`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ body, parent_id: parentId })
  });
  await assertOk(response, "Comment could not be posted.");
  return ((await response.json()) as { comment: NovelComment }).comment;
}

/** Soft-deletes a comment (author or novel owner only). Returns the tombstone. */
export async function deleteComment(
  novelId: string,
  commentId: string,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<NovelComment> {
  const response = await fetcher(
    `${apiBaseUrl}/api/novels/${encodeURIComponent(novelId)}/comments/${encodeURIComponent(commentId)}`,
    { method: "DELETE" }
  );
  await assertOk(response, "Comment could not be deleted.");
  return ((await response.json()) as { comment: NovelComment }).comment;
}

/**
 * Moderation (novel owner only): removes every comment by `userId` on the novel,
 * plus replies left on them. Returns how many of the user's comments were removed.
 */
export async function purgeUserComments(
  novelId: string,
  userId: string,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<number> {
  const response = await fetcher(
    `${apiBaseUrl}/api/novels/${encodeURIComponent(novelId)}/comments/purge`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: userId })
    }
  );
  await assertOk(response, "Could not remove the comments.");
  return ((await response.json()) as { removed: number }).removed;
}

/** A user the signed-in author has blocked from commenting, with their byline. */
export interface BlockedUser {
  user_id: string;
  display_name: string;
  avatar_url: string | null;
}

/** The users the signed-in author has blocked from commenting on their novels. */
export async function loadBlockedUsers(
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<BlockedUser[]> {
  const response = await fetcher(`${apiBaseUrl}/api/blocks`);
  if (!response.ok) throw new Error(`Blocked-users request failed with ${response.status}.`);
  return ((await response.json()) as { blocked?: BlockedUser[] }).blocked ?? [];
}

/** Blocks a user from commenting on the signed-in author's novels. */
export async function blockUser(
  userId: string,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<void> {
  const response = await fetcher(`${apiBaseUrl}/api/blocks`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ user_id: userId })
  });
  await assertOk(response, "Could not block the user.");
}

/** Unblocks a previously blocked user. */
export async function unblockUser(
  userId: string,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<void> {
  const response = await fetcher(`${apiBaseUrl}/api/blocks/${encodeURIComponent(userId)}`, {
    method: "DELETE"
  });
  await assertOk(response, "Could not unblock the user.");
}

/** Likes (POST) or unlikes (DELETE) a comment. Returns the updated comment. */
export async function setCommentLike(
  novelId: string,
  commentId: string,
  liked: boolean,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<NovelComment> {
  const response = await fetcher(
    `${apiBaseUrl}/api/novels/${encodeURIComponent(novelId)}/comments/${encodeURIComponent(commentId)}/like`,
    { method: liked ? "POST" : "DELETE" }
  );
  await assertOk(response, "Could not update the like.");
  return ((await response.json()) as { comment: NovelComment }).comment;
}

// ── Notifications ───────────────────────────────────────────────────────────

/** An in-app author notification, enriched with the actor byline + novel title. */
export interface AppNotification {
  id: string;
  type: "comment" | "reply" | "like";
  novel_id: string;
  /** The comment that triggered it (comment/reply), or null for a like. */
  comment_id: string | null;
  actor_id: string;
  created_at: string;
  read: boolean;
  actor: AuthorByline;
  novel_title: string | null;
}

export interface NotificationsResult {
  notifications: AppNotification[];
  unreadCount: number;
}

/** Loads the signed-in author's notifications (newest-first) + unread count. */
export async function loadNotifications(
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<NotificationsResult> {
  const response = await fetcher(`${apiBaseUrl}/api/notifications`);
  if (!response.ok) {
    throw new Error(`Notifications request failed with ${response.status}.`);
  }
  return (await response.json()) as NotificationsResult;
}

/** Marks one notification read; returns the new unread count. */
export async function markNotificationRead(
  id: string,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<number> {
  const response = await fetcher(
    `${apiBaseUrl}/api/notifications/${encodeURIComponent(id)}/read`,
    { method: "POST" }
  );
  if (!response.ok) {
    throw new Error(`Mark-read request failed with ${response.status}.`);
  }
  return ((await response.json()) as { unreadCount: number }).unreadCount;
}

/** Marks all of the author's notifications read. */
export async function markAllNotificationsRead(
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<void> {
  const response = await fetcher(`${apiBaseUrl}/api/notifications/read-all`, { method: "POST" });
  if (!response.ok) {
    throw new Error(`Mark-all-read request failed with ${response.status}.`);
  }
}

/**
 * The per-novel support link is legacy (it moved to the author profile) and no
 * longer editable, yet the API still validates it on save — so a stored link
 * that has become invalid is dropped rather than blocking every save for good.
 */
function withoutInvalidSupportLink(novel: Novel): Novel {
  if (novel.support_link === undefined || isValidSupportLink(novel.support_link)) return novel;
  const rest = { ...novel };
  delete rest.support_link;
  return rest;
}

export async function saveNovel(
  novel: Novel,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<Novel> {
  const response = await fetcher(`${apiBaseUrl}/api/novels/${encodeURIComponent(novel.id)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(withoutInvalidSupportLink(novel))
  });

  await assertOk(response, "Novel save request failed.");

  const body = (await response.json()) as { novel: Novel };
  cacheMutatedNovel(body.novel);
  return body.novel;
}

export async function deleteNovel(
  id: string,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<void> {
  const response = await fetcher(`${apiBaseUrl}/api/novels/${encodeURIComponent(id)}`, {
    method: "DELETE"
  });

  await assertOk(response, "Delete request failed.");
  removeNovelCache(id);
  // The cached lists still contain the deleted novel — drop them so a later
  // offline hydration can't resurrect it.
  invalidateListCaches();
}

export async function publishNovel(
  id: string,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<Novel> {
  const response = await fetcher(
    `${apiBaseUrl}/api/novels/${encodeURIComponent(id)}/publish`,
    { method: "POST" }
  );

  await assertOk(response, "Publish request failed.");

  const body = (await response.json()) as { novel: Novel };
  // Normalize/strip like the read paths do, so callers that render the returned
  // novel directly (e.g. the detail page's setNovel) get the same shape as a load.
  const novel = stripStateSystem(normalizeNovel(body.novel));
  cacheMutatedNovel(novel);
  return novel;
}

/**
 * Keeps the offline copy at the just-mutated version (preserving the last-seen
 * stats) and drops the list caches, which still describe the pre-mutation
 * state.
 */
function cacheMutatedNovel(novel: Novel): void {
  const stats = readNovelCache<CachedNovelEntry>(novel.id)?.stats ?? EMPTY_STATS;
  writeNovelCache(novel.id, { novel, stats });
  invalidateListCaches();
}

export async function createGenerationJob(
  input: CreateGenerationJobInput,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<GenerationJob> {
  const response = await fetcher(`${apiBaseUrl}/api/generation-jobs`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input)
  });

  await assertOk(response, "Generation job request failed.");

  const body = (await response.json()) as { job: GenerationJob };
  return body.job;
}

/**
 * Queue a job that machine-translates a novel into `languages` (one credit unit
 * per language). Returns the running job to poll like any generation job.
 */
export async function createTranslateJob(
  novelId: string,
  languages: string[],
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<GenerationJob> {
  const response = await fetcher(
    `${apiBaseUrl}/api/novels/${encodeURIComponent(novelId)}/translate`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ languages })
    }
  );

  await assertOk(response, "Translation request failed.");

  const body = (await response.json()) as { job: GenerationJob };
  return body.job;
}

/**
 * Starts a continuation ("догенерация") job that grows a finished novel past
 * one of its endings. Returns the queued job; poll it like any generation job.
 */
export async function extendNovel(
  novelId: string,
  input: { fromEndingId: string; guidance?: string },
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<GenerationJob> {
  const response = await fetcher(`${apiBaseUrl}/api/novels/${encodeURIComponent(novelId)}/extend`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ from_ending_id: input.fromEndingId, guidance: input.guidance })
  });

  await assertOk(response, "Continuation request failed.");

  const body = (await response.json()) as { job: GenerationJob };
  return body.job;
}

// ─── Agentic AI edit ──────────────────────────────────────────────────────────

/** One turn of the planning conversation, replayed to the model each call. */
export interface AiEditTurn {
  role: "author" | "assistant";
  text: string;
}

/** A single approved change, resolved by the server against the real novel. */
export type AiEditStep =
  | {
      kind: "scene";
      scene_id: string;
      scene_title: string;
      instruction: string;
      origin: "scene" | "range" | "branch";
    }
  | {
      kind: "choice";
      scene_id: string;
      scene_title: string;
      choice_id: string;
      current_text: string;
      new_text: string;
    }
  | { kind: "character"; character_id: string; character_name: string; instruction: string };

/**
 * A single approved STRUCTURAL change (graph topology). Mirrors the server's
 * `StructuralStep`; applied through the apply-structural route, which re-validates
 * the whole graph. Display fields (titles, current_text) are re-derived server-side.
 */
export type AiStructuralStep =
  | {
      op: "add_scene";
      from_scene_id: string;
      from_title: string;
      label: string;
      title: string;
      instruction: string;
      leads_to?: string;
      leads_to_title?: string;
      /** Label of the new scene's own choice onward (with leads_to). */
      continue_label?: string;
      /** Plan-local name; later steps refer to the new scene as `new:<ref>`. */
      ref?: string;
      character_ids?: string[];
      character_names?: string[];
      location_asset_id?: string;
      location_name?: string;
    }
  | { op: "edit_scene"; scene_id: string; scene_title: string; instruction: string }
  | { op: "add_choice"; scene_id: string; scene_title: string; label: string; to_scene_id: string; to_title: string }
  | {
      op: "retarget_choice";
      scene_id: string;
      scene_title: string;
      choice_id: string;
      current_text: string;
      to_scene_id: string;
      to_title: string;
    }
  | { op: "remove_choice"; scene_id: string; scene_title: string; choice_id: string; current_text: string }
  | { op: "edit_choice"; scene_id: string; scene_title: string; choice_id: string; current_text: string; text: string }
  | { op: "delete_scene"; scene_id: string; scene_title: string }
  | { op: "add_character"; name: string; gender: string; age: number; traits: string[]; appearance: string; ref?: string }
  | { op: "delete_character"; character_id: string; character_name: string }
  | { op: "edit_character"; character_id: string; character_name: string; instruction: string }
  | {
      op: "set_character_fields";
      character_id: string;
      character_name: string;
      name?: string;
      gender?: string;
      age?: number;
      traits?: string[];
    }
  | {
      op: "set_scene_characters";
      scene_id: string;
      scene_title: string;
      /** true: `character_ids` is the whole new cast; false: `add` / `remove` adjust it. */
      replace?: boolean;
      character_ids: string[];
      character_names: string[];
      add?: string[];
      remove?: string[];
      added_names?: string[];
      removed_names?: string[];
    }
  | { op: "rename_location"; asset_id: string; from_name: string; name: string }
  | { op: "set_scene_location"; scene_id: string; scene_title: string; asset_id: string; location_name: string }
  | { op: "restage_scene"; scene_id: string; scene_title: string }
  | {
      op: "set_section_staging";
      scene_id: string;
      scene_title: string;
      frame_count: number;
      changes: Array<{
        from_frame: number;
        to_frame: number;
        on_stage?: string[];
        on_stage_names?: string[];
        location_asset_id?: string;
        location_name?: string;
        emotions?: Record<string, string>;
      }>;
    }
  | { op: "rename_scene"; scene_id: string; from_title: string; title: string }
  | { op: "set_start_scene"; scene_id: string; scene_title: string }
  | {
      op: "set_ending_meta";
      scene_id: string;
      scene_title: string;
      ending_title?: string;
      ending_type?: "good" | "bad" | "neutral";
      ending_description?: string;
      can_continue?: boolean;
    }
  | { op: "set_novel_meta"; title?: string; description?: string; genre?: string[]; tone?: string };

/** A single approved MEDIA (image regeneration) step, applied client-side. */
export type AiMediaStep =
  // `prompt`: the planner's replacement image prompt — the picture is painted
  // from it and it becomes the asset's stored prompt. Absent = same prompt.
  | { kind: "cover"; prompt?: string }
  | { kind: "background"; scene_id: string; scene_title: string; prompt?: string }
  | { kind: "portrait"; character_id: string; character_name: string }
  | { kind: "all"; count: number }
  // A new art style (the author's short note), then every image repainted in it.
  | { kind: "art_style"; notes: string; count: number };

/**
 * How much the author has delegated to the planner. "careful" (default) asks a
 * clarifying question when a request is ambiguous; "fast" tells the planner to
 * decide for itself and always propose a plan.
 */
export type AiEditMode = "careful" | "fast";

export type AiEditPlanResponse =
  | { status: "needs_clarification"; questions: string[] }
  | { status: "plan" | "empty"; summary: string; steps: AiEditStep[]; rejected: string[] }
  | {
      /** The plan changes the graph structure and/or regenerates images. */
      status: "structural";
      summary: string;
      structural_steps: AiStructuralStep[];
      media_steps: AiMediaStep[];
      rejected: string[];
    }
  | {
      /**
       * The author asked to rebuild the whole novel from its original idea. The
       * server read the seed from the stored novel; the editor confirms, then
       * starts a full generation job from it.
       */
      status: "regenerate";
      summary: string;
      idea: string;
      language: string;
      tone: string;
      /** The author's stored genre, when the novel was generated with one. */
      genre?: string;
      /** The author's stored length preset, when the novel recorded one. */
      length?: "short" | "medium" | "long";
    };

/**
 * Plan an edit from one free-text instruction. Cheap — one model call, no prose
 * is written — so the author can iterate on the plan before approving it.
 */
export async function planAiEdit(
  novelId: string,
  input: { instruction: string; transcript?: AiEditTurn[]; mode?: AiEditMode; novel?: Novel },
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<AiEditPlanResponse> {
  const response = await fetcher(
    `${apiBaseUrl}/api/novels/${encodeURIComponent(novelId)}/ai-edit/plan`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        instruction: input.instruction,
        transcript: input.transcript ?? [],
        mode: input.mode ?? "careful",
        // The editor's current (maybe unsaved) draft, so the plan matches it.
        ...(input.novel ? { novel: input.novel } : {})
      })
    }
  );

  await assertOk(response, "Planning failed.");

  return (await response.json()) as AiEditPlanResponse;
}

export interface AiEditApplyResult {
  scene_texts: Record<string, string>;
  /**
   * New choice labels, nested scene id → choice id → text. Choice ids repeat
   * across scenes, so a label must only ever be applied inside the scene the
   * approved step named.
   */
  choice_texts: Record<string, Record<string, string>>;
  character_fields: Record<string, CharacterRewriteFields>;
  applied: number;
  /** Names of steps that could not be produced; the rest still applied. */
  failed: string[];
  /** Approved steps that stopped resolving against the novel before they ran. */
  rejected: string[];
}

/**
 * Execute the steps the author approved. Nothing is persisted — the editor
 * patches the results into memory and a later save writes them.
 */
export async function applyAiEdit(
  novelId: string,
  steps: AiEditStep[],
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<AiEditApplyResult> {
  const response = await fetcher(
    `${apiBaseUrl}/api/novels/${encodeURIComponent(novelId)}/ai-edit/apply`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ steps })
    }
  );

  await assertOk(response, "Applying the plan failed.");

  return (await response.json()) as AiEditApplyResult;
}

export interface AiStructuralApplyResult {
  /** The reassembled, server-validated novel. Swap it into the editor in memory. */
  novel: Novel;
  applied: number;
  /** Ops that could not be applied (unknown id, cap, would break the graph). */
  rejected: string[];
  /** Ids minted for the plan's new scenes / characters: `new:<ref>` → id. */
  refs: Record<string, string>;
  /** Characters whose approved description change did not land (skip their repaint). */
  unchangedCharacters: string[];
}

/**
 * Execute approved STRUCTURAL steps. Sends the author's CURRENT novel (so unsaved
 * edits survive) and the steps; the server assembles + validates a new graph and
 * returns it. Nothing is persisted — the editor swaps the returned novel into
 * memory and a later save writes it.
 */
export async function applyStructuralAiEdit(
  novelId: string,
  novel: Novel,
  steps: AiStructuralStep[],
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<AiStructuralApplyResult> {
  const response = await fetcher(
    `${apiBaseUrl}/api/novels/${encodeURIComponent(novelId)}/ai-edit/apply-structural`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ novel, steps })
    }
  );

  await assertOk(response, "Applying the structural plan failed.");

  const data = (await response.json()) as {
    novel: Novel;
    applied: number;
    rejected?: string[];
    refs?: Record<string, string>;
    unchanged_characters?: string[];
  };
  return {
    // Same read-boundary normalisation as loadNovel, so the swapped-in novel
    // matches what the editor loaded.
    novel: stripStateSystem(normalizeNovel(data.novel)),
    applied: data.applied,
    rejected: data.rejected ?? [],
    refs: data.refs ?? {},
    unchangedCharacters: data.unchanged_characters ?? []
  };
}

/** Character fields an AI rewrite proposes. */
export interface CharacterRewriteFields {
  traits: string[];
  appearance: string;
}

/**
 * Regenerates one character's neutral portrait via the real image provider,
 * using the given prompt (typically the editor's current, possibly-unsaved
 * prompt). Returns the updated portrait asset plus the `providerName` that
 * produced it — the novel is NOT persisted server-side; the editor patches the
 * asset in memory and a later save persists the new URL. A `providerName` of
 * "placeholder" means the real generator was unavailable and a generic image
 * was returned, so the caller can warn before saving. Throws with the server's
 * message on failure (e.g. 429 rate limit, or a failed render). Real providers
 * spend image credits per call.
 */
export async function regeneratePortrait(
  novelId: string,
  characterId: string,
  input: { prompt: string; artStyle?: string },
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<{ asset: Asset; providerName: string }> {
  const response = await fetcher(
    `${apiBaseUrl}/api/novels/${encodeURIComponent(novelId)}/characters/${encodeURIComponent(characterId)}/regenerate-portrait`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ prompt: input.prompt, artStyle: input.artStyle })
    }
  );

  await assertOk(response, "Portrait regeneration failed.");

  const body = (await response.json()) as { asset: Asset; providerName?: string };
  return { asset: body.asset, providerName: body.providerName ?? "" };
}

/** What "Generate emotions" returns: a fresh neutral base + the rendered expressions. */
export interface GeneratedCharacterEmotions {
  /** The new neutral portrait (same asset id as the character's portrait). */
  neutral: Pick<Asset, "id" | "url" | "prompt" | "status">;
  /** emotion → its sprite asset (id follows the portrait id scheme). Only the ones that rendered. */
  emotions: Partial<Record<Emotion, Pick<Asset, "id" | "url" | "status">>>;
  providerName: string;
}

/**
 * Generates expression sprites for one character (paid: `character_emotions`) —
 * a fresh neutral portrait plus up to three emotions edited from it. Never
 * persists server-side: the editor patches the result in, a later save keeps it.
 */
export async function generateCharacterEmotions(
  novelId: string,
  characterId: string,
  input: { emotions: Emotion[]; prompt: string; artStyle?: string },
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<GeneratedCharacterEmotions> {
  const response = await fetcher(
    `${apiBaseUrl}/api/novels/${encodeURIComponent(novelId)}/characters/${encodeURIComponent(characterId)}/generate-emotions`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ emotions: input.emotions, prompt: input.prompt, artStyle: input.artStyle })
    }
  );

  await assertOk(response, "Emotion generation failed.");

  const body = (await response.json()) as Partial<GeneratedCharacterEmotions>;
  return {
    neutral: body.neutral!,
    emotions: body.emotions ?? {},
    providerName: body.providerName ?? ""
  };
}

/**
 * Regenerates the novel cover via the real image provider. Same contract as
 * {@link regeneratePortrait}: returns the updated asset + `providerName`, does
 * not persist server-side, and spends image credits per call.
 */
export async function regenerateCover(
  novelId: string,
  input: { prompt: string; artStyle?: string },
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<{ asset: Asset; providerName: string }> {
  const response = await fetcher(
    `${apiBaseUrl}/api/novels/${encodeURIComponent(novelId)}/regenerate-cover`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ prompt: input.prompt, artStyle: input.artStyle })
    }
  );

  await assertOk(response, "Cover regeneration failed.");

  const body = (await response.json()) as { asset: Asset; providerName?: string };
  return { asset: body.asset, providerName: body.providerName ?? "" };
}

/**
 * Regenerates one scene's background via the real image provider. Same contract
 * as {@link regeneratePortrait}: returns the updated asset + `providerName`,
 * does not persist server-side, and spends image credits per call.
 */
export async function regenerateBackground(
  novelId: string,
  sceneId: string,
  input: { prompt: string; artStyle?: string },
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<{ asset: Asset; providerName: string }> {
  const response = await fetcher(
    `${apiBaseUrl}/api/novels/${encodeURIComponent(novelId)}/scenes/${encodeURIComponent(sceneId)}/regenerate-background`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ prompt: input.prompt, artStyle: input.artStyle })
    }
  );

  await assertOk(response, "Background regeneration failed.");

  const body = (await response.json()) as { asset: Asset; providerName?: string };
  return { asset: body.asset, providerName: body.providerName ?? "" };
}

/**
 * Expand a short art-style note ("аниме") into the full style descriptor. No
 * image render, no persistence — the editor uses it to drive "regenerate all
 * images" under a changed style, then Save persists it.
 */
export async function expandArtStyle(
  novelId: string,
  notes: string,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<{ art_style: string; art_style_notes: string }> {
  const response = await fetcher(
    `${apiBaseUrl}/api/novels/${encodeURIComponent(novelId)}/expand-art-style`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ notes })
    }
  );
  await assertOk(response, "Art-style expansion failed.");
  const body = (await response.json()) as { art_style?: string; art_style_notes?: string };
  return { art_style: body.art_style ?? "", art_style_notes: body.art_style_notes ?? notes };
}

export async function loadGenerationJob(
  id: string,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<GenerationJob> {
  // Status polls repeat anyway; a bound keeps a hung request from blocking the
  // poll loop (both poll sites swallow the rejection and try again).
  const response = await fetcher(`${apiBaseUrl}/api/generation-jobs/${encodeURIComponent(id)}`, {
    signal: readTimeoutSignal(LIST_READ_TIMEOUT_MS)
  });

  if (!response.ok) {
    throw new ApiError(response.status, undefined, `Generation job request failed with ${response.status}.`);
  }

  const body = (await response.json()) as { job: GenerationJob };
  return body.job;
}

/**
 * True when a job lookup failed because the job is really gone — pruned (404)
 * or not the caller's (403) — as opposed to the API being briefly unreachable
 * (a deploy restarts it; network errors, 5xx, timeouts). Only a gone job may be
 * forgotten; a transient failure must keep tracking the still-running pipeline.
 */
export function isGoneJobError(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 404 || error.status === 403);
}

export async function cancelGenerationJob(
  id: string,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<GenerationJob> {
  const response = await fetcher(
    `${apiBaseUrl}/api/generation-jobs/${encodeURIComponent(id)}/cancel`,
    { method: "POST" }
  );

  await assertOk(response, "Generation job cancel failed.");

  const body = (await response.json()) as { job: GenerationJob };
  return body.job;
}

export async function retryGenerationJob(
  id: string,
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<GenerationJob> {
  const response = await fetcher(
    `${apiBaseUrl}/api/generation-jobs/${encodeURIComponent(id)}/retry`,
    { method: "POST" }
  );

  await assertOk(response, "Generation job retry failed.");

  const body = (await response.json()) as { job: GenerationJob };
  return body.job;
}

/**
 * Reads the active LLM provider name from /api/health ("mock", "openai",
 * "anthropic"). Returns null when the API is unreachable or the field is
 * missing, so callers can hide provider-specific UI.
 */
export async function loadLlmProviderName(
  fetcher: Fetcher = authedFetch,
  apiBaseUrl = getApiBaseUrl()
): Promise<string | null> {
  try {
    const response = await fetcher(`${apiBaseUrl}/api/health`);
    if (!response.ok) {
      throw new Error(`Health request failed with ${response.status}.`);
    }

    const body = (await response.json()) as { llm_provider?: unknown };
    return typeof body.llm_provider === "string" && body.llm_provider.length > 0
      ? body.llm_provider
      : null;
  } catch {
    return null;
  }
}

export function toNovelSummary(novel: Novel): NovelSummary {
  return {
    id: novel.id,
    title: novel.title,
    description: novel.description,
    visibility: novel.visibility,
    genre: novel.genre,
    tone: novel.tone,
    cover_asset_id: novel.cover_asset_id,
    cover_asset_url:
      novel.assets.find((asset) => asset.id === novel.cover_asset_id)?.url ?? null,
    owner_id: novel.owner_id,
    updated_at: novel.updated_at,
    views: 0,
    likes: 0,
    completions: 0,
    language: novel.language,
    available_languages: Object.keys(novel.translations ?? {}),
    ...(novel.short_code ? { short_code: novel.short_code } : {})
  };
}

export function getApiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? defaultApiBaseUrl;
}
